import { types as api } from '@mesa-labs/mesa-api';
import { Button } from '@mesa-labs/mesa-ui';
import React, { useState } from 'react';

import { useActionItemDraft } from '../hooks';
import {
  Controls, Form, Input, Select,
} from './InlineEntryForm';
import { AssigneeOptions } from './SelectOptions';
import SourceNoteQuote from './SourceNoteQuote';
import { ErrorText } from './styles';

type Props = {
  me?: string;
  operators: readonly api.VendorActionItemAssigneeResponse[];
  // Create-from-Note: previews the Note and records it as the item's source; nothing is prefilled.
  sourceNote?: api.VendorNoteResponse;
  // A failure from the last attempt, reported by the caller's mutation.
  error?: string;
  onCreate: (request: api.CreateVendorActionItemRequest) => Promise<unknown>;
};

// Compact create form for tight surfaces such as the Enablement expanded row; new items always start as To Do.
export default function InlineCreateActionItem({
  me, operators, sourceNote, error, onCreate,
}: Props): React.ReactElement {
  const draft = useActionItemDraft({ initialAssignee: me, sourceNote, onCreate });
  // Validation messages wait for the first submit attempt.
  const [tried, setTried] = useState(false);
  const submit = () => { setTried(true); draft.submit(); };

  return (
    <Form>
      {sourceNote && <SourceNoteQuote note={sourceNote} maxHeight={60} />}
      <Input autoFocus placeholder="What needs to happen?" value={draft.title} onChange={(e) => draft.setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
      <Controls>
        <Select aria-label="Assignee" value={draft.assignedTo} onChange={(e) => draft.setAssignedTo(e.target.value)}>
          {!me && <option value="" disabled>Assign to… (required)</option>}
          <AssigneeOptions operators={operators} me={me} />
        </Select>
        <Button text="Create" fontSize={12} height={26} isLoading={draft.saving} onClick={submit} />
      </Controls>
      {tried && !draft.assignedTo && <ErrorText>Pick an assignee</ErrorText>}
      {tried && !draft.title.trim() && <ErrorText>Title is required</ErrorText>}
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Form>
  );
}
